/**
 * workflows/updateWorkflow.ts
 *
 * UPDATE workflow: re-validate and re-bind metadata for existing files.
 * Files are already in the creative folder — only metadata edit + BL update.
 *
 * This is a standalone workflow (not merged with CONVERT).
 */

import Notifications from 'react-notification-system-redux';
import { api } from '../../../../general/apiScenarios';
import { CHNode } from '../../../../general/node';
import { ListenerApiLight } from '../../../listenerMiddleware';
import { loadBLContext } from '../blContextService';
import {
  _closeEditor,
  _setSubmitInProgress,
  requestUserValidation,
} from '../metadataEditor/MetadataEditorService';
import { JobMode } from '../legacy_models/model';
import type {
  PreparableFile,
  PreparedFile,
  FileExecutionResult,
  CreativeBLSettings,
} from '../contracts';
import type { Fallback } from '../legacy_models/fallback';
import * as fileManagerActions from '../../fileManager/actions';

function extractApiMessage(errorData: any): string | null {
  const responseData = errorData?.response?.data;

  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data.join('; ');
  }
  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  return null;
}

async function loadOccupiedFilenames(folderNodeId: number, ignoredNames: string[] = []) {
  const result = await api.fs.loadNodes(folderNodeId);
  if (!result.success) {
    return [] as string[];
  }

  const ignored = new Set(ignoredNames);
  return result.data.response.nodes
    .filter((node) => node.type !== 'folder')
    .map((node) => node.name)
    .filter((name) => !ignored.has(name));
}

function buildFailedDetails(preparedFiles: PreparedFile[], failed: FileExecutionResult[]): string {
  return failed
    .filter((result): result is Extract<FileExecutionResult, { success: false }> => !result.success)
    .map((result) => {
      const file = preparedFiles.find((preparedFile) => preparedFile.id === result.fileId);
      const fileName = file?.newFilename ?? file?.originalName ?? result.fileId;
      return `${fileName}: ${result.error}`;
    })
    .join('; ');
}

export interface UpdateWorkflowOptions {
  mode?: JobMode;
  fallback?: Fallback;
  preparableFiles?: PreparableFile[];
  notify?: boolean;
  successTitle?: string;
  errorTitle?: string;
  errorMessage?: string | ((failedCount: number) => string);
}

// ─── Workflow ────────────────────────────────────────────────────────────────

/**
 * Update metadata for existing files in a creative folder.
 *
 * @param lapi  Listener API for dispatch/signal
 * @param nodes  Files currently in the creative folder
 * @param creativeNodeId  Node ID of the creative folder
 */
export async function updateWorkflow(
  lapi: ListenerApiLight,
  nodes: CHNode[],
  creativeNodeId: number,
  options: UpdateWorkflowOptions = {},
): Promise<boolean> {
  // 1. Load BL context
  const ctxResult = await loadBLContext(lapi, creativeNodeId);
  lapi.signal.throwIfAborted();

  if (!ctxResult.success) {
    if (options.notify !== false) {
      lapi.dispatch(
        Notifications.error({ title: 'Error', message: 'Failed to load creative context' }),
      );
    }
    return false;
  }

  const context: CreativeBLSettings = ctxResult.data;

  // 2. Convert to PreparableFile
  const preparableFiles: PreparableFile[] =
    options.preparableFiles ??
    nodes.map((node) => ({
      id: String(node.id),
      originalName: node.name,
      relpath: '',
      nodeRef: node,
    }));

  const occupiedFilenames = await loadOccupiedFilenames(
    creativeNodeId,
    preparableFiles.map((file) => file.originalName),
  );
  lapi.signal.throwIfAborted();

  // 3. Open MetadataEditor (UPDATE mode keeps existing meta)
  const preparedFiles = await requestUserValidation(preparableFiles, context, {
    mode: options.mode ?? JobMode.UPDATE,
    fallback: options.fallback,
    occupiedFilenames,
  });

  if (!preparedFiles) return false;

  try {
    lapi.signal.throwIfAborted();

    // 4. Execute: rename (if needed) + update BL metadata
    const results: FileExecutionResult[] = [];
    for (const file of preparedFiles) {
      lapi.signal.throwIfAborted();

      const nodeId = file.nodeRef?.id;
      if (!nodeId) {
        results.push({ success: false, fileId: file.id, error: 'No node reference' });
        continue;
      }

      // Rename if filename changed
      if (file.originalName !== file.newFilename) {
        const renameResult = await api.fs.rename(file.newFilename, nodeId);
        if (!renameResult.success) {
          results.push({
            success: false,
            fileId: file.id,
            error: extractApiMessage(renameResult.errorData) ?? 'Rename failed',
          });
          continue;
        }
      }

      const blResult = file.nodeRef?.is_linked
        ? await api.bl.blUpdateCreativeFile(nodeId, file.meta, context.creative.id)
        : await api.bl.blCreateCreativeFile(nodeId, context.creative.id, file.meta);

      if (!blResult.success) {
        results.push({
          success: false,
          fileId: file.id,
          error: file.nodeRef?.is_linked ? 'BL update failed' : 'BL attach failed',
        });
        continue;
      }

      lapi.dispatch(
        fileManagerActions.nodePatched({
          nodeID: nodeId,
          patch: { name: file.newFilename, is_linked: true },
        }),
      );

      results.push({ success: true, nodeId });
    }

    // 5. Report
    const failed = results.filter((r) => !r.success);
    if (failed.length === 0) {
      lapi.dispatch(fileManagerActions.refreshRequested());
      if (options.notify !== false) {
        lapi.dispatch(
          Notifications.success({
            title: options.successTitle ?? 'All creatives have been updated successfully',
          }),
        );
      }
    } else {
      if (options.notify !== false) {
        const failedDetails = buildFailedDetails(preparedFiles, failed);
        const errorMessage =
          typeof options.errorMessage === 'function'
            ? options.errorMessage(failed.length)
            : options.errorMessage ?? `Failed to update ${failed.length} file(s)`;

        lapi.dispatch(
          Notifications.error({
            title: options.errorTitle ?? 'Update error',
            message: failedDetails ? `${errorMessage}. ${failedDetails}` : errorMessage,
          }),
        );
      }
      return false;
    }

    return true;
  } finally {
    _setSubmitInProgress(false);
    _closeEditor();
  }
}
